import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { SearchablePickerField } from '@/components/ui/SearchablePickerField';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { getErrorMessage } from '@/lib/functions';
import { supabase } from '@/lib/supabase';
import { createServiceRequest, fetchRequest, pickAndUploadRequestImage, updateServiceRequest } from '@/lib/serviceRequests';
import { SuccessOverlay } from '@/components/SuccessOverlay';
import { ErrorText } from '@/components/ErrorText';

function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Απλό keyword-matching: μαντεύει κατηγορία από τον τίτλο — π.χ. "άλλαγή σόλας"
 * ταιριάζει με κατηγορία "Επισκευές & Συντήρηση" αν καμιά λέξη της κατηγορίας
 * (ή του τίτλου) εμφανίζεται στην άλλη πλευρά. Απλά ένα βοηθητικό preset — ο
 * χρήστης πάντα μπορεί να διαλέξει άλλη κατηγορία χειροκίνητα. */
function guessCategoryId(title: string, categories: { id: number; name: string }[]): number | null {
  const t = normalize(title);
  if (!t.trim()) return null;
  for (const cat of categories) {
    const catWords = normalize(cat.name).split(/[^a-zα-ω0-9]+/).filter((w) => w.length >= 4);
    if (catWords.some((w) => t.includes(w))) return cat.id;
  }
  return null;
}

export default function NewRequestScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { targetProfessionalId, targetProfessionalName, presetTitle, editId } = useLocalSearchParams<{
    targetProfessionalId?: string;
    targetProfessionalName?: string;
    presetTitle?: string;
    editId?: string;
  }>();
  const isDirect = !!targetProfessionalId;
  const isEdit = !!editId;
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { user } = useAuth();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [title, setTitle] = useState(presetTitle ?? '');
  const [description, setDescription] = useState('');
  const [professionCategoryId, setProfessionCategoryId] = useState<number | null>(null);
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [regionIndex, setRegionIndex] = useState<number | null>(-1);
  const [regions, setRegions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);

  useEffect(() => {
    void supabase.from('profession_categories').select('id, name').order('name')
      .then(({ data }) => { if (data) setCategories(data as { id: number; name: string }[]); });
    // Δεν έχει νόημα για ιδιωτικό αίτημα προς συγκεκριμένο επαγγελματία — η
    // περιοχή χρησιμεύει μόνο στον δημόσιο πίνακα/χάρτη αιτημάτων.
    if (!isDirect) {
      void supabase.from('greek_regions').select('name').order('name')
        .then(({ data }) => { if (data) setRegions((data as { name: string }[]).map((r) => r.name)); });
    }
  }, [isDirect]);

  // Προφόρτωση υπάρχοντος αιτήματος σε λειτουργία επεξεργασίας.
  useEffect(() => {
    if (!editId) return;
    void fetchRequest(editId).then((r) => {
      if (!r) return;
      setTitle(r.title);
      setDescription(r.description ?? '');
      setProfessionCategoryId(r.profession_category_id);
      setImageUrl(r.image_url);
      setPendingRegionName(r.region);
    });
  }, [editId]);

  // "Όλες" (id -1) είναι η προεπιλογή — αίτημα ορατό σε επαγγελματίες κάθε
  // περιοχής. Δεν προσυμπληρώνουμε την περιοχή του προφίλ: αν ο χρήστης
  // αφήσει "Όλες", θα ειδοποιηθούν όλοι οι σχετικοί επαγγελματίες, όχι μόνο
  // της δικής του περιοχής.
  const regionOptions = useMemo(
    () => [{ id: -1, name: t('requests.regionAll') }, ...regions.map((name, id) => ({ id, name }))],
    [regions, t],
  );

  // Μόλις φορτωθεί η λίστα περιοχών, βρίσκουμε τον index που αντιστοιχεί στο
  // αποθηκευμένο όνομα περιοχής του αιτήματος υπό επεξεργασία (ή "Όλες" αν
  // ήταν null).
  const [pendingRegionName, setPendingRegionName] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (pendingRegionName === undefined || regions.length === 0) return;
    setRegionIndex(pendingRegionName == null ? -1 : regions.indexOf(pendingRegionName));
    setPendingRegionName(undefined);
  }, [pendingRegionName, regions]);

  // Μαντεύει κατηγορία από τον τίτλο όσο ο χρήστης πληκτρολογεί, εκτός αν την
  // έχει ήδη διαλέξει χειροκίνητα.
  useEffect(() => {
    if (isEdit || isDirect || categoryTouched || categories.length === 0) return;
    const guess = guessCategoryId(title, categories);
    if (guess != null) setProfessionCategoryId(guess);
  }, [title, categories, isEdit, isDirect, categoryTouched]);

  async function doPickImage(source: 'library' | 'camera') {
    if (!user) return;
    setError(null);
    setImageBusy(true);
    try {
      const url = await pickAndUploadRequestImage(user.id, source);
      if (url) setImageUrl(url);
    } catch (e) {
      setError(getErrorMessage(e, t('requests.errorImageFailed')));
    } finally {
      setImageBusy(false);
    }
  }

  function onPickImage() {
    appAlert(
      t('requests.action_addImage'),
      undefined,
      [
        { text: t('requests.imageSourceCamera'), onPress: () => void doPickImage('camera') },
        { text: t('requests.imageSourceLibrary'), onPress: () => void doPickImage('library') },
        { text: t('common.cancel'), style: 'cancel' },
      ],
    );
  }

  async function submit() {
    setError(null);
    if (!title.trim()) {
      setError(t('requests.errorTitleRequired'));
      return;
    }
    if (!isDirect && professionCategoryId == null) {
      setError(t('requests.errorCategoryRequired'));
      return;
    }
    setBusy(true);
    try {
      const region = !isDirect && regionIndex != null && regionIndex >= 0 ? regions[regionIndex] : null;
      if (isEdit) {
        await updateServiceRequest(editId!, {
          title: title.trim(),
          description: description.trim() || null,
          professionCategoryId,
          imageUrl,
          region,
        });
        router.replace({ pathname: '/(app)/requests/[id]', params: { id: editId! } } as any);
      } else {
        const id = await createServiceRequest({
          title: title.trim(),
          description: description.trim() || null,
          professionCategoryId: isDirect ? null : professionCategoryId,
          targetProfessionalId: isDirect ? targetProfessionalId! : null,
          imageUrl,
          region,
        });
        setCreatedId(id);
        setShowSuccess(true);
      }
    } catch (e) {
      setError(getErrorMessage(e, t('requests.errorCreateFailed')));
      setBusy(false);
    }
  }

  return (
    <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView contentContainerStyle={[styles.content, { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl }]}>
        <Pressable onPress={() => router.back()} style={styles.back}>
          <Text style={styles.backText}>{t('common.back')}</Text>
        </Pressable>

        <Text style={styles.title}>
          {isEdit ? t('requests.editTitle') : isDirect ? t('requests.directTitle') : t('requests.newTitle')}
        </Text>
        <Text style={styles.subtitle}>
          {isEdit
            ? t('requests.editSubtitle')
            : isDirect
            ? t('requests.directSubtitle', { name: targetProfessionalName || t('common.user') })
            : t('requests.newSubtitle')}
        </Text>

        <View style={styles.field}>
          <Text style={styles.label}>{t('requests.field_title')}</Text>
          <TextInput
            style={styles.input}
            value={title}
            onChangeText={setTitle}
            placeholder={t('requests.placeholder_title')}
            placeholderTextColor={colors.textMuted}
            maxLength={120}
          />
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('requests.field_description')}</Text>
          <TextInput
            style={[styles.input, styles.multiline]}
            value={description}
            onChangeText={setDescription}
            placeholder={t('requests.placeholder_description')}
            placeholderTextColor={colors.textMuted}
            multiline
            maxLength={1000}
          />
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('requests.field_imageOptional')}</Text>
          {imageUrl ? (
            <View style={styles.imagePreviewWrap}>
              <Image source={{ uri: imageUrl }} style={styles.imagePreview} />
              <Pressable style={styles.imageRemoveBtn} onPress={() => setImageUrl(null)} hitSlop={8} accessibilityRole="button">
                <Ionicons name="close-circle" size={26} color={colors.danger} />
              </Pressable>
            </View>
          ) : (
            <Pressable style={styles.imagePickBtn} onPress={onPickImage} disabled={imageBusy} accessibilityRole="button">
              {imageBusy ? (
                <ActivityIndicator color={colors.primary} />
              ) : (
                <>
                  <Ionicons name="camera-outline" size={18} color={colors.primary} />
                  <Text style={styles.imagePickBtnText}>{t('requests.action_addImage')}</Text>
                </>
              )}
            </Pressable>
          )}
          <Text style={styles.imageHint}>{t('requests.imageHint')}</Text>
        </View>

        {!isDirect && categories.length > 0 && (
          <SearchablePickerField
            label={t('requests.field_category')}
            value={professionCategoryId}
            options={categories}
            onChange={(id) => { setCategoryTouched(true); setProfessionCategoryId(id); }}
          />
        )}

        {!isDirect && regionOptions.length > 0 && (
          <SearchablePickerField
            label={t('requests.field_regionOptional')}
            value={regionIndex}
            options={regionOptions}
            onChange={setRegionIndex}
          />
        )}

        <ErrorText>{error}</ErrorText>

        <Button
          title={isEdit ? t('common.save') : isDirect ? t('requests.action_sendDirect') : t('requests.action_publish')}
          loading={busy}
          onPress={submit}
        />
      </ScrollView>

      <SuccessOverlay
        visible={showSuccess}
        variant="antenna"
        message={t('requests.postedSuccess')}
        onDone={() => {
          setShowSuccess(false);
          if (createdId) router.replace({ pathname: '/(app)/requests/[id]', params: { id: createdId } } as any);
        }}
      />
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    content: {
      // Σε μεγάλες οθόνες η φόρμα μένει σε αναγνώσιμο πλάτος αντί να
      // τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
      width: '100%',
      maxWidth: contentMaxWidth.form,
      alignSelf: 'center',
      paddingHorizontal: spacing.lg,
      gap: spacing.md,
    },
    back: { alignSelf: 'flex-start' },
    backText: { color: colors.primary, fontSize: fontSize.md, fontWeight: '600' },
    title: { color: colors.text, fontSize: fontSize.xl, fontWeight: '700' },
    subtitle: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 20 },
    field: { gap: 6 },
    label: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600' },
    input: {
      backgroundColor: colors.surfaceAlt, borderRadius: radius.md, borderWidth: 1,
      borderColor: colors.border, paddingHorizontal: spacing.md, height: 48,
      color: colors.text, fontSize: fontSize.md,
    },
    multiline: { height: 100, paddingTop: spacing.sm, textAlignVertical: 'top' },
    error: { color: colors.danger, fontSize: fontSize.sm },
    imagePickBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.border,
      borderStyle: 'dashed',
      borderRadius: radius.md,
      paddingVertical: 12,
    },
    imagePickBtnText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '700' },
    imagePreviewWrap: { alignSelf: 'flex-start', position: 'relative' },
    imagePreview: { width: 120, height: 120, borderRadius: radius.md, backgroundColor: colors.surfaceAlt },
    imageRemoveBtn: {
      position: 'absolute',
      top: -8,
      right: -8,
      backgroundColor: colors.background,
      borderRadius: radius.full,
    },
    imageHint: { color: colors.textMuted, fontSize: fontSize.xs, lineHeight: 16 },
  });
}
